import axios from 'axios';
import { UnificationHttpClient } from '@/lib/repositories';

export interface HubSpotContactUpdatePayload {
  first_name: string
  last_name: string
  phone: string
}

/**
 * Loads Keycloak configuration dynamically.
 *
 * Dynamic import helps reduce the initial bundle size
 * by loading the Keycloak config only when required.
 */
const getKeycloakConfig = async () => {
  const keycloak = await import('@/lib/plugins/keycloak-config');

  const { authServerUrl, realm, authenticated } = keycloak.default;

  return {
    /**
     * Base Keycloak realm endpoint
     * Example:
     * https://auth.example.com/realms/my-realm
     */
    baseURL: `${authServerUrl}realms/${realm}`,

    /**
     * Current authentication state
     */
    authenticated,
    keycloak: keycloak.default,
  };
};

const getAuthenticatedKeycloakConfig = async () => {
  const { baseURL, keycloak } = await getKeycloakConfig();

  try {
    await keycloak.updateToken(30);
  } catch (error) {
    console.error('Failed to refresh token:', error);
    keycloak.login();
    throw error;
  }

  return {
    baseURL,
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
    },
  };
};

/**
 * Returns current date in YYYY-MM-DD format.
 *
 * Example:
 * 2026-05-11
 */
const getCurrentDate = (): string => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Updates the user's `firstLoginAt` custom attribute.
 *
 * Keycloak custom attributes are stored as string arrays,
 * therefore the value is wrapped inside an array.
 *
 * @param profile - Existing user profile object
 */
export const updateFirstLoginAt = async (profile: any) => {
  const payload = {
    ...profile,
    attributes: {
      ...profile.attributes,

      /**
       * Stores the first login date in YYYY-MM-DD format
       */
      firstLoginAt: [getCurrentDate()],
    },
  };

  return updateUserProfile(payload);
};

/**
 * Updates the Keycloak user profile.
 *
 * @param payload - Updated profile payload
 */
export const updateUserProfile = async (payload: any) => {
  const { baseURL, headers } = await getAuthenticatedKeycloakConfig();

  return axios.post(`${baseURL}/account`, payload, { headers });
};

/**
 * Syncs profile name changes to HubSpot so contact records stay up to date.
 */
export const updateHubSpotInfo = async (payload: HubSpotContactUpdatePayload) => {
  return UnificationHttpClient.post('hubspot_contacts/update', payload);
};

/**
 * Fetches user credentials from Keycloak account API.
 */
export const getCredentials = async () => {
  const { baseURL, headers } = await getAuthenticatedKeycloakConfig();

  return axios.get(`${baseURL}/account/credentials`, { headers });
};

/**
 * Removes a specific credential by its ID.
 */
export const removeCredential = async (credentialId: string) => {
  const { baseURL, headers } = await getAuthenticatedKeycloakConfig();

  return axios.delete(`${baseURL}/account/credentials/${credentialId}`, { headers });
};